import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API, { BASE_DEFAULT_IMAGE_URL, getloginUserId } from "../api/axios";
import Layoutsecond from "../components/Layoutsecond";
import { SlLike } from "react-icons/sl";
import { FaRegCommentDots } from "react-icons/fa";
import { GoShareAndroid } from "react-icons/go";

export default function DiscoverDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [liked, setLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [errors, setErrors] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Image loading state
  const [imageState, setImageState] = useState({
    src: BASE_DEFAULT_IMAGE_URL,
    attempt: 0,
    isLoading: false
  });

  const loginUserId = getloginUserId();

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token && !!loginUserId);
  }, [loginUserId]);

  useEffect(() => {
    fetchFeedItem();
  }, [id]);

  // Handle image loading with timeout
  useEffect(() => {
    if (!item) return;

    let primaryTimeout;
    let fallbackTimeout;
    let startTimeout;

    // Reset image state
    setImageState({
      src: BASE_DEFAULT_IMAGE_URL,
      attempt: 0,
      isLoading: false
    });

    // Start trying images after brief moment
    startTimeout = setTimeout(() => {
      if (item.images) {
        // Try primary image first
        setImageState({
          src: item.images,
          attempt: 1,
          isLoading: true
        });

        // Set 5.5 second timeout for primary image
        primaryTimeout = setTimeout(() => {
          setImageState(prev => {
            // Only switch if still on attempt 1 and still loading
            if (prev.attempt === 1 && prev.isLoading) {
              if (item.source?.logo_url) {
                return {
                  src: item.source.logo_url,
                  attempt: 2,
                  isLoading: true
                };
              } else {
                return {
                  src: BASE_DEFAULT_IMAGE_URL,
                  attempt: 3,
                  isLoading: false
                };
              }
            }
            return prev;
          });
        }, 5500);

      } else if (item.source?.logo_url) {
        // No primary image, try fallback directly
        setImageState({
          src: item.source.logo_url,
          attempt: 2,
          isLoading: true
        });

        // Set 5.5 second timeout for fallback image
        fallbackTimeout = setTimeout(() => {
          setImageState(prev => {
            if (prev.attempt === 2 && prev.isLoading) {
              return {
                src: BASE_DEFAULT_IMAGE_URL,
                attempt: 3,
                isLoading: false
              };
            }
            return prev;
          });
        }, 5500);
      }
    }, 50);

    // Cleanup timeouts
    return () => {
      if (startTimeout) clearTimeout(startTimeout);
      if (primaryTimeout) clearTimeout(primaryTimeout);
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, [item]);

  // Handle timeout for attempt 2 (fallback image)
  useEffect(() => {
    if (!item) return;
    
    let fallbackTimeout;
    
    if (imageState.attempt === 2 && imageState.isLoading) {
      fallbackTimeout = setTimeout(() => {
        setImageState(prev => {
          if (prev.attempt === 2 && prev.isLoading) {
            return {
              src: BASE_DEFAULT_IMAGE_URL,
              attempt: 3,
              isLoading: false
              
            };
          }
          return prev;
        });
      }, 5500);
    }

    return () => {
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, [imageState.attempt, imageState.isLoading, item]);

  const fetchFeedItem = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/feed-items/${id}`);
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setItem(data);
        setLiked(data.is_liked || false);
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error("Failed to fetch feed item details:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const requireLogin = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return false;
    }
    return true;
  };

  const handleLike = async () => {
    if (!requireLogin()) return;

    const originalLiked = liked;
    setLiked(!liked);

    try {
      const res = await API.post(`/feed-items/${id}/like`, {
        user_id: loginUserId,
      });

      if (res.data.success) {
        setLiked(res.data.liked);
        setItem((prev) => ({
          ...prev,
          likes_count: res.data.likes_count,
        }));
      } else {
        setLiked(originalLiked);
      }
    } catch (err) {
      console.error("Error liking post:", err);
      setLiked(originalLiked);
    }
  };

  const handleComment = async () => {
    if (!requireLogin()) return;
    if (!newComment.trim()) return;

    setErrors({});
    const commentToPost = newComment.trim();

    const tempComment = {
      id: Date.now(),
      comment: commentToPost,
      user: { name: "You" },
      created_at: new Date().toISOString(),
      is_temp: true,
    };

    setComments((prev) => [tempComment, ...prev]);
    setNewComment("");

    setItem((prev) => ({
      ...prev,
      comments_count: (prev.comments_count || 0) + 1,
    }));

    try {
      const res = await API.post(`/feed-items/${id}/comment`, {
        user_id: loginUserId,
        comment: commentToPost,
      });

      if (res.data.success) {
        const createdComment = res.data.comment;
        setComments((prev) =>
          prev.filter((c) => !c.is_temp).concat(createdComment)
        );

        setItem((prev) => ({
          ...prev,
          comments_count: res.data.comments_count || prev.comments_count,
        }));
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.errors) {
        setErrors(err.response.data.errors);
      } else {
        console.error("Error adding comment:", err);
      }

      setComments((prev) => prev.filter((c) => !c.is_temp));
      setNewComment(commentToPost);
      setItem((prev) => ({
        ...prev,
        comments_count: Math.max((prev.comments_count || 1) - 1, 0),
      }));
    }
  };

  const handleShare = async () => {
    if (!item) return;

    const shareUrl = window.location.href;
    const shareTitle = item.title || "Check this out";
    const shareText = item.description
      ? item.description.replace(/<[^>]+>/g, "").slice(0, 100) + "..."
      : "Interesting content";

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });

        if (isLoggedIn) {
          await API.post(`/feed-items/${id}/share`, { user_id: loginUserId });
        }
      } else {
        setShowShareModal(true);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error sharing:", err);
      }
    }
  };

  const ShareModal = () => {
    const shareUrl = window.location.href;
    const shareTitle = item?.title || 'Check this out';

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
        setShowShareModal(false);
        
        if (isLoggedIn) {
          await API.post(`/feed-items/${id}/share`, { user_id: loginUserId });
        }
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Link copied to clipboard!');
        setShowShareModal(false);
        
        if (isLoggedIn) {
          await API.post(`/feed-items/${id}/share`, { user_id: loginUserId });
        }
      }
    };

    const shareOnSocialMedia = (platform) => {
      let socialShareUrl = '';
      
      switch (platform) {
        case 'facebook':
          socialShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          break;
        case 'twitter':
          socialShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
          break;
        case 'linkedin':
          socialShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
          break;
        case 'whatsapp':
          socialShareUrl = `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`;
          break;
        case 'telegram':
          socialShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
          break;
        default:
          return;
      }
      
      window.open(socialShareUrl, '_blank', 'width=600,height=400');
      setShowShareModal(false);
      
      if (isLoggedIn) {
        API.post(`/feed-items/${id}/share`, { user_id: loginUserId });
      }
    };

    if (!showShareModal) return null;

    return (
      <div className="modal-backdrop" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div className="modal-content" style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '400px',
          width: '90%'
        }}>
          <div className="modal-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h5 style={{ margin: 0 }}>Share this post</h5>
            <button 
              onClick={() => setShowShareModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          </div>

          <div className="share-options" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <button 
              onClick={() => shareOnSocialMedia('facebook')}
              className="btn btn-primary"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}
            >
              <span style={{ fontSize: '24px', marginBottom: '5px' }}>f</span>
              <small>Facebook</small>
            </button>

            <button 
              onClick={() => shareOnSocialMedia('twitter')}
              className="btn btn-info"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}
            >
              <span style={{ fontSize: '24px', marginBottom: '5px' }}>𝕏</span>
              <small>Twitter</small>
            </button>

            <button 
              onClick={() => shareOnSocialMedia('linkedin')}
              className="btn btn-primary"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}
            >
              <span style={{ fontSize: '24px', marginBottom: '5px' }}>in</span>
              <small>LinkedIn</small>
            </button>

            <button 
              onClick={() => shareOnSocialMedia('whatsapp')}
              className="btn btn-success"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}
            >
              <span style={{ fontSize: '24px', marginBottom: '5px' }}>📱</span>
              <small>WhatsApp</small>
            </button>

            <button 
              onClick={() => shareOnSocialMedia('telegram')}
              className="btn btn-info"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}
            >
              <span style={{ fontSize: '24px', marginBottom: '5px' }}>✈️</span>
              <small>Telegram</small>
            </button>

            <button 
              onClick={copyToClipboard}
              className="btn btn-secondary"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}
            >
              <span style={{ fontSize: '24px', marginBottom: '5px' }}>📋</span>
              <small>Copy Link</small>
            </button>
          </div>

          <div className="share-url" style={{
            marginBottom: '15px'
          }}>
            <label className="small text-muted">Share URL:</label>
            <div style={{
              display: 'flex',
              gap: '10px'
            }}>
              <input 
                type="text" 
                value={shareUrl}
                readOnly
                className="form-control"
                style={{ fontSize: '12px' }}
              />
              <button 
                onClick={copyToClipboard}
                className="btn btn-outline-primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleImageLoad = () => {
    setImageState(prev => ({
      ...prev,
      isLoading: false
    }));
  };

  const handleImageError = () => {
    if (!item) return;
    
    const currentAttempt = imageState.attempt;
    
    if (currentAttempt === 1 && item.source?.logo_url) {
      // Primary image failed immediately, try fallback
      setImageState({
        src: item.source.logo_url,
        attempt: 2,
        isLoading: true
      });
    } else if (currentAttempt === 2 || (currentAttempt === 1 && !item.source?.logo_url)) {
      // Fallback failed or no fallback available, show default
      setImageState({
        src: BASE_DEFAULT_IMAGE_URL,
        attempt: 3,
        isLoading: false
      });
    }
  };

  if (loading) {
    return (
      <Layoutsecond>
        <p>Loading feed details...</p>
      </Layoutsecond>
    );
  }

  if (!item) {
    return (
      <Layoutsecond>
        <p>Feed item not found.</p>
      </Layoutsecond>
    );
  }

  return (
    <Layoutsecond>
      <div className="discover-details">
        <h2 className="discover-title">{item.title}</h2>
        <span className="discover-category-title small">
            {item.topic?.name || "Uncategorized"}
          </span>
        <div className="discover-description-img">
          
          <img
            alt={item.title}
            src={imageState.src}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        </div>

        <div className="discover-description">
          {item.description ? (
            <div dangerouslySetInnerHTML={{ __html: item.description }} />
          ) : (
            <p>No description available.</p>
          )}
        </div>

        {item.link && (
          <div className="discover-description-more mt-3 flex justify-center">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-white border border-gray-200 text-black hover:bg-gray-50 shadow-none py-1.5 px-3 text-sm w-auto inline-block"
            >
              View Original
            </a>
          </div>
        )}

        {/* Dynamic Counts Section */}
        <div className="discover-card-icon mt-3 flex gap-2">
          <button
            onClick={handleLike}
            className={`btn bg-white border border-gray-200 text-black hover:bg-gray-50 shadow-none ${liked ? "active" : ""}`}
          >
            <SlLike />
            <span className="small">{item.likes_count || 0} Likes</span>
          </button>

          <button className="btn bg-white border border-gray-200 text-black hover:bg-gray-50 shadow-none">
            <FaRegCommentDots />
            <span className="small">
              {item.comments_count || comments.length} Comments
            </span>
          </button>

          <button className="btn bg-white border border-gray-200 text-black hover:bg-gray-50 shadow-none" onClick={handleShare}>
            <GoShareAndroid />
            <span className="small">Share</span>
          </button>
        </div>

        {/* Comments Section */}
        <div className="comments-section mt-4">
          <h4>Comments ({comments.length})</h4>

          <div className="comment-input mt-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className={`form-control ${errors.comment ? "is-invalid" : ""}`}
              rows="3"
            />
            {errors.comment && (
              <div className="invalid-feedback">{errors.comment[0]}</div>
            )}
            <button
              onClick={handleComment}
              className="btn btn-primary mt-3"
              disabled={!newComment.trim()}
            >
              Post Comment
            </button>
          </div>

          {/* Comments List */}
          <div className="comments-list mt-4">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <div
                  key={comment.id || comment.temp_id}
                  className="comment-item mt-3 p-3 border rounded"
                >
                  <div className="comment-item-img-name flex gap-3 items-start">
                    <img
                      src={comment.user?.avatar || BASE_DEFAULT_IMAGE_URL}
                      alt={comment.user?.name || "Anonymous"}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="comment flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-sm">
                          {comment.user?.name || "Anonymous"}
                        </strong>
                        <span className="date small text-gray-500">
                          {formatDate(comment.created_at)}
                        </span>
                        {comment.is_temp && (
                          <span className="text-xs text-blue-500">
                            Posting...
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 m-0">{comment.comment}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="mt-3 text-gray-500 text-center">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && <ShareModal />}
    </Layoutsecond>
  );
}